import { amcLeftNavTheme, Theme, useTheme } from "@amx/ux-twoui-theming";
import { getKeyboardEventKeyCode, useMount } from "@amx/ux-twoui-utils";
import { KeyCodes, Stack, ThemeProvider } from "@fluentui/react";
import { ThrowOnUndefinedColorContext } from "@m365-admin/customizations";
import { INavGroupProps, INavLink, INavLinkGroup, INavStyleProps, Nav } from "@m365-admin/nav";
import React, { useEffect, useRef, useState } from "react";
import { ViewportType } from "../../shared/constants";
import { IconName } from "../../styles/icons";
import { useViewport } from "../../styles/media";
import { registerIconsTwoUi } from "../Icon/Icon";
import { getNavStyles, navWrapperPaddingStyles, navWrapperStyle } from "./LeftNav.styles";
import { LeftNavHeaderProps, LeftNavLinkProps, LeftNavProps } from "./LeftNav.types";
import { LeftNavBypassBlocksButton } from "./LeftNavBypassBlocksButton/LeftNavBypassBlocksButton";
import { LeftNavProfile } from "./LeftNavHeaders/LeftNavProfile/LeftNavProfile";
import { LeftNavProfileShimmer } from "./LeftNavHeaders/LeftNavProfileShimmer/LeftNavProfileShimmer";
import { getLeftNavDirectLink } from "./LeftNavLinks/LeftNavDirectLink/LeftNavDirectLink";

export const isSmallViewport = (viewportType: ViewportType): boolean => {
    return viewportType === "s";
};

export const isSmallToLargeViewport = (viewportType: ViewportType): boolean => {
    return ["s", "m", "l"].includes(viewportType);
};

export const isExtraLargeOrBiggerViewport = (viewportType: ViewportType): boolean => {
    return ["xl", "xxl", "xxxl"].includes(viewportType);
};

export const LeftNav: React.FC<LeftNavProps> = ({
    bypassBlocksButton,
    expandCollapseNavButton,
    navHeader,
    navLinkGroups,
    selectedNavLinkKey,
    removeNavButtonBorder = false
}) => {
    const theme = useTheme();
    const viewportType = useViewport();
    const previousViewportType = useRef<ViewportType>(viewportType);
    const [isNavCollapsed, setIsNavCollapsed] = useState(isSmallToLargeViewport(viewportType));

    useMount(() => {
        // This ensures the LeftNav uses the provided icons as TwoUi icons (instead of Fabric icons) when possible
        const icons = navLinkGroups.map(group => group.navLinks.map(navLink => navLink.icon as IconName)).flat();
        registerIconsTwoUi(icons);
    });

    useEffect(() => {
        // LeftNav is always expanded in extra-large-or-bigger viewports
        if (isExtraLargeOrBiggerViewport(viewportType)) {
            setIsNavCollapsed(false);
        }
        // LeftNav collapses when a user switches from an extra-large-or-bigger viewport to a small-to-large one
        if (isExtraLargeOrBiggerViewport(previousViewportType.current) && isSmallToLargeViewport(viewportType)) {
            setIsNavCollapsed(true);
        }
        previousViewportType.current = viewportType;
    }, [viewportType]);

    const onExpandCollapseButtonClick = () => {
        setIsNavCollapsed(!isNavCollapsed);
    };

    const navWrapperRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isExtraLargeOrBiggerViewport(viewportType) || isNavCollapsed) {
            return;
        }

        /**
         * Detect when a click is outside the Left Nav itself and collapse it.
         */
        const softCollapseOnOutsideClick = (ev: MouseEvent) => {
            if (ev.target instanceof Node && navWrapperRef.current && !navWrapperRef.current.contains(ev.target)) {
                setIsNavCollapsed(true);
            }
        };

        const softCollapseOnOutsideFocus = (ev: FocusEvent) => {
            if (ev.target instanceof Node && navWrapperRef.current && !navWrapperRef.current.contains(ev.target)) {
                setIsNavCollapsed(true);
            }
        };

        /**
         * Detect when the Escape key is pressed and collapse the Left Nav.
         */
        const softCollapseOnEscape = (ev: KeyboardEvent) => {
            const keyCode = getKeyboardEventKeyCode(ev);
            if (keyCode === KeyCodes.escape || (typeof keyCode === "string" && keyCode.toLowerCase() === "escape")) {
                setIsNavCollapsed(true);
            }
        };

        window.addEventListener("click", softCollapseOnOutsideClick);
        window.addEventListener("keydown", softCollapseOnEscape);
        window.addEventListener("focusin", softCollapseOnOutsideFocus);

        return () => {
            // remove event listeners on cleanup
            window.removeEventListener("click", softCollapseOnOutsideClick);
            window.removeEventListener("keydown", softCollapseOnEscape);
            window.removeEventListener("focusin", softCollapseOnOutsideFocus);
        };
    }, [viewportType, isNavCollapsed, navWrapperRef]);

    const groups: INavLinkGroup[] = [];
    navLinkGroups.forEach((navLinkGroup, index) => {
        const group = {
            key: navLinkGroup.key,
            name: navLinkGroup.title,
            links: createNavLinks(navLinkGroup.navLinks, selectedNavLinkKey, isNavCollapsed, theme),
            styles: {
                navGroupTitle: { fontSize: theme.fonts.smallPlus.fontSize }
            }
        };
        groups.push(index !== 0 ? group : { ...group, onRenderGroupTitle: (props?: INavGroupProps) => onRenderGroupTitle(navHeader, props) });
    });

    const onThrowOnUndefinedColor = (args: {
        desiredColor: string | undefined;
        desiredColorName: string;
        callingComponentName: string;
    }) => {
        return amcLeftNavTheme.semanticColors[args.desiredColorName];
    };

    return (
        <ThemeProvider theme={amcLeftNavTheme}>
            <ThrowOnUndefinedColorContext.Provider value={{ onThrowOnUndefinedColor, disableThrowOnUndefinedColor: true }}>
                <Stack>
                    <Stack styles={navWrapperPaddingStyles(viewportType)} />
                    <div ref={navWrapperRef} style={navWrapperStyle(isNavCollapsed, viewportType)}>
                        <LeftNavBypassBlocksButton {...bypassBlocksButton} />
                        <Nav
                            styles={(props: INavStyleProps) => getNavStyles(props, theme, viewportType, removeNavButtonBorder)}
                            groups={groups}
                            hideCollapseLink={isExtraLargeOrBiggerViewport(viewportType)}
                            collapseNavLinkProps={expandCollapseNavButton}
                            isNavCollapsed={isNavCollapsed}
                            onNavCollapsed={onExpandCollapseButtonClick}
                        />
                    </div>
                </Stack>
            </ThrowOnUndefinedColorContext.Provider>
        </ThemeProvider>
    );
};

const onRenderGroupTitle = (header: LeftNavHeaderProps, props?: INavGroupProps): JSX.Element => {
    switch (header.headerType) {
        case "profileHeader":
            return (
                <LeftNavProfile {...header} isNavCollapsed={!!props?.isNavCollapsed} />
            );
        case "profileHeaderShimmer":
            return (
                <LeftNavProfileShimmer {...header} isNavCollapsed={!!props?.isNavCollapsed} />
            );
    }
};

// tslint:disable-next-line: readonly-array // Nav component takes a non-readonly array
const createNavLinks = (navLinks: readonly LeftNavLinkProps[], selectedNavLinkKey: string, isNavCollapsed: boolean, theme: Theme): INavLink[] => {
    return navLinks.map((navLink: LeftNavLinkProps, index): INavLink => {
        const isSelected = navLink.itemKey === selectedNavLinkKey;

        switch (navLink.linkType) {
            case "directlink":
                return getLeftNavDirectLink({ ...navLink, isSelected }, index === navLinks.length - 1, isNavCollapsed, theme);
        }
    });
};
